import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { ImportDocumentTypeCount } from './importTypes'
import { EmptyState } from '../ui/empty-state'
import { useDocumentTypes } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'

interface ImportDocumentTypeBarsProps {
    org: string
    importUid: string
    documentTypes: ImportDocumentTypeCount[]
}

/**
 * Per-document-type breakdown (spec §7.4, left column): one horizontal bar per
 * type, widest first, each linking to the documents list filtered by this
 * import and this type. The summary carries the type CODE only, so it is
 * resolved to the org's document type uid and its localized name here (the
 * code is the fallback for a type the org no longer has, #1790); the
 * unclassified bar filters on documents with no type.
 */
export function ImportDocumentTypeBars({ org, importUid, documentTypes }: ImportDocumentTypeBarsProps) {
    const { t } = useTranslation('imports')
    const { locale } = useLocale()
    const { data: orgDocumentTypes = [] } = useDocumentTypes(org)
    const uidByCode = new Map(orgDocumentTypes.map(dt => [dt.code, dt.uid]))
    const nameByCode = new Map(orgDocumentTypes.map(dt => [
        dt.code,
        typeof dt.name === 'string' ? dt.name : getLocalizedName(dt.name, locale),
    ]))

    if (documentTypes.length === 0) {
        return (
            <section data-testid="import-document-types" className="space-y-3">
                <h2 className="text-sm font-medium text-muted-foreground">{t('document_types.title')}</h2>
                <EmptyState title={t('document_types.empty')} />
            </section>
        )
    }

    const max = Math.max(...documentTypes.map(dt => dt.count), 1)

    return (
        <section data-testid="import-document-types" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('document_types.title')}</h2>
            <div className="space-y-2 rounded-lg border border-border bg-card p-4">
                {documentTypes.map(dt => (
                    <Link
                        key={dt.code || '__unclassified__'}
                        to="/$org/documents"
                        params={{ org }}
                        search={{
                            asset_uid: undefined,
                            document_type_uid: dt.code ? uidByCode.get(dt.code) : undefined,
                            flags: undefined,
                            import_uid: importUid,
                            unclassified: dt.code ? undefined : true,
                            limit: 100,
                        }}
                        data-testid={`import-doc-type-${dt.code || 'unclassified'}`}
                        className="block rounded-md px-1 py-1 transition-colors hover:bg-muted/60"
                    >
                        <div className="flex items-baseline justify-between gap-2 text-sm">
                            <span className="truncate text-foreground">
                                {dt.code
                                    ? (nameByCode.get(dt.code) || dt.code)
                                    : t('document_types.unclassified')}
                            </span>
                            <span className="tabular-nums text-muted-foreground">{dt.count}</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${(dt.count / max) * 100}%` }}
                            />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    )
}
